import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getApiUrl } from '../config';
import { Match, Player, MatchReport } from '../types';

interface MatchScore {
  team1Goals: number;
  team2Goals: number;
  team1Name: string;
  team2Name: string;
}

interface Teams {
  team1: (Player | null)[];
  team2: (Player | null)[];
}

interface UserInfo {
  id: string;
  email: string;
}

export const useMatchData = (matchId?: string) => {
  const { id: paramsId } = useParams();
  const id = matchId || paramsId;
  const [match, setMatch] = useState<Match | null>(null);
  const [teams, setTeams] = useState<Teams | null>(null);
  const [matchReport, setMatchReport] = useState<MatchReport | null>(null);
  const [matchScore, setMatchScore] = useState<MatchScore | null>(null);
  const [reportCompleted, setReportCompleted] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [userIsCreator, setUserIsCreator] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  // Utilidad para obtener todos los jugadores
  const getAllPlayers = useCallback((match: any) => {
    if (match.allPlayers) return match.allPlayers;
    if (match.players && match.substitutes) return [...match.players, ...match.substitutes];
    if (match.players) return match.players;
    return [];
  }, []);

  // Procesar equipos con posiciones específicas
  const processTeamWithPositions = useCallback((teamArray: any[], teamLength: number, playersMap: Map<string, Player>) => {
    const result = new Array(teamLength).fill(null);
    
    teamArray.forEach((playerData: any) => {
      if (typeof playerData === 'object' && playerData.playerId) {
        // Nuevo formato con posiciones
        const player = playersMap.get(playerData.playerId);
        if (player && playerData.position >= 0 && playerData.position < teamLength) {
          result[playerData.position] = player;
        }
      } else if (typeof playerData === 'string') {
        // Formato antiguo (solo ID)
        const player = playersMap.get(playerData);
        if (player) {
          // Encontrar la primera posición disponible
          const firstEmptyIndex = result.findIndex(p => p === null);
          if (firstEmptyIndex !== -1) {
            result[firstEmptyIndex] = player;
          }
        }
      }
    });
    
    return result;
  }, []);

  // Construir equipos desde datos del match
  const buildTeamsFromMatch = useCallback((matchData: Match) => {
    const playersPerTeam = matchData.maxPlayers || 5;
    const team1Length = playersPerTeam;
    const team2Length = playersPerTeam;

    let team1: (Player | null)[] = [];
    let team2: (Player | null)[] = [];
    
    // Si hay equipos guardados, usarlos
    if (matchData.lastTeams && matchData.lastTeams.team1 && matchData.lastTeams.team2) {
      const allPlayers = getAllPlayers(matchData);
      const playersMap = new Map<string, Player>(allPlayers.map((player: Player) => [player.id, player]));
      
      team1 = processTeamWithPositions(matchData.lastTeams.team1, team1Length, playersMap);
      team2 = processTeamWithPositions(matchData.lastTeams.team2, team2Length, playersMap);
      
      // Agregar jugadores que no están en ningún equipo (nuevos jugadores)
      const usedPlayerIds = new Set();
      matchData.lastTeams.team1.forEach((playerData: any) => {
        if (typeof playerData === 'object' && playerData.playerId) {
          usedPlayerIds.add(playerData.playerId);
        } else if (typeof playerData === 'string') {
          usedPlayerIds.add(playerData);
        }
      });
      matchData.lastTeams.team2.forEach((playerData: any) => {
        if (typeof playerData === 'object' && playerData.playerId) {
          usedPlayerIds.add(playerData.playerId);
        } else if (typeof playerData === 'string') {
          usedPlayerIds.add(playerData);
        }
      });
      
      const unassigned = allPlayers.filter((p: Player) => !usedPlayerIds.has(p.id));
      // Distribuir solo jugadores principales no asignados (NO suplentes)
      const unassignedMainPlayers = unassigned.filter((p: Player) => !p.isSubstitute);
      unassignedMainPlayers.forEach((player: Player) => {
        if (team1.filter(p => p !== null).length < playersPerTeam && (team1.filter(p => p !== null).length <= team2.filter(p => p !== null).length)) {
          const firstEmptyIndex = team1.findIndex(p => p === null);
          if (firstEmptyIndex !== -1) {
            team1[firstEmptyIndex] = player;
          }
        } else if (team2.filter(p => p !== null).length < playersPerTeam) {
          const firstEmptyIndex = team2.findIndex(p => p === null);
          if (firstEmptyIndex !== -1) {
            team2[firstEmptyIndex] = player;
          }
        }
      });
    } else {
      // Si no hay equipos guardados, crear arrays del tamaño correcto y distribuir automáticamente
      team1 = new Array(playersPerTeam).fill(null);
      team2 = new Array(playersPerTeam).fill(null);
      
      // Solo distribuir jugadores principales (no suplentes).
      // Defensive: WebSocket events ocasionalmente traen un `match` sin `players`
      // (eventos parciales). Sin este guard la app crasheaba.
      const safePlayers = Array.isArray(matchData.players) ? matchData.players : [];
      const mainPlayers = safePlayers.filter((p: Player) => !p.isSubstitute);
      mainPlayers.forEach((player: Player) => {
        if (team1.filter(p => p !== null).length < playersPerTeam && (team1.filter(p => p !== null).length <= team2.filter(p => p !== null).length)) {
          const firstEmptyIndex = team1.findIndex(p => p === null);
          if (firstEmptyIndex !== -1) {
            team1[firstEmptyIndex] = player;
          }
        } else if (team2.filter(p => p !== null).length < playersPerTeam) {
          const firstEmptyIndex = team2.findIndex(p => p === null);
          if (firstEmptyIndex !== -1) {
            team2[firstEmptyIndex] = player;
          }
        }
      });
    }
    
    return { team1, team2 };
  }, [getAllPlayers, processTeamWithPositions]);

  // Fetch match data
  const fetchMatch = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await fetch(getApiUrl(`/api/matches/${id}`));
      if (!response.ok) throw new Error('Failed to fetch match');
      const data = await response.json();
      setMatch(data);
      
      const teamsData = buildTeamsFromMatch(data);
      setTeams(teamsData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching match:', error);
    }
  }, [id, buildTeamsFromMatch]);

  // Fetch match report
  const fetchMatchReport = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await fetch(getApiUrl(`/api/matches/${id}/report`));
      if (response.ok) {
        const data = await response.json();
        setMatchReport(data);
        setMatchScore(data.matchScore || null);
        setReportCompleted(data.reportCompleted || false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching match report:', error);
    }
  }, [id]);

  // Check creator status
  const checkCreatorStatus = useCallback(async () => {
    if (!id) return;
    
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(getApiUrl(`/api/matches/${id}/check-creator`), {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data.userInfo);
        setUserIsCreator(data.isCreator);
      } else {
        setUserInfo(null);
        setUserIsCreator(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error checking creator status:', error);
      setUserInfo(null);
      setUserIsCreator(false);
    } finally {
      setIsLoadingUser(false);
    }
  }, [id]);

  // Load data on mount
  useEffect(() => {
    fetchMatch();
    fetchMatchReport();
    checkCreatorStatus();
  }, [fetchMatch, fetchMatchReport, checkCreatorStatus]);

  return {
    // Data
    match,
    teams,
    matchReport,
    matchScore,
    reportCompleted,
    userInfo,
    userIsCreator,
    isLoadingUser,
    loadingReport,
    
    // Actions
    setMatch,
    setTeams,
    setMatchReport,
    setMatchScore,
    setReportCompleted,
    setLoadingReport,
    setUserIsCreator,
    fetchMatch,
    fetchMatchReport,
    buildTeamsFromMatch,
    getAllPlayers
  };
};
